import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { segurosApi } from '../../../lib/api';

export function AvisoEmailDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    segurosApi.getRecordatorioConfig().then((r) => setCfg({ ...r.data, to: (r.data.to || []).join(', '), cc: (r.data.cc || []).join(', ') }));
    segurosApi.previewRecordatorio().then((r) => setPreview(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        dias_antes: Number(cfg.dias_antes) || 5,
        hora: cfg.hora || '09:00',
        to: cfg.to.split(',').map((s) => s.trim()).filter(Boolean),
        cc: cfg.cc.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await segurosApi.putRecordatorioConfig(payload);
      toast.success('✓ Configuración de aviso guardada');
      onClose?.();
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await segurosApi.sendRecordatorioNow();
      if (r.data.sent) toast.success(`✓ Aviso enviado (${r.data.count} cuota(s))`);
      else toast.warning(`No enviado: ${r.data.reason === 'no_recipients' ? 'configura destinatarios primero' : r.data.reason === 'no_items' ? 'no hay cuotas por vencer' : r.data.reason}`);
    } catch (e) {
      toast.error(`Error: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-aviso-email">
        <DialogHeader>
          <DialogTitle className="text-base">Aviso de vencimiento por correo</DialogTitle>
          <DialogDescription className="text-xs">
            Envío diario automático con las cuotas que vencen dentro de los próximos N días. Cada cuota se avisa una sola vez.
            {preview && <> Actualmente hay <b>{preview.count}</b> cuota(s) dentro del plazo.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Aviso automático activado</Label>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="switch-aviso-enabled" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Días de anticipación</Label>
              <Input type="number" min="1" max="30" value={cfg.dias_antes} onChange={(e) => setCfg((c) => ({ ...c, dias_antes: e.target.value }))} className="h-8 text-sm" data-testid="input-dias-antes" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hora de envío</Label>
              <Input type="time" value={cfg.hora} onChange={(e) => setCfg((c) => ({ ...c, hora: e.target.value }))} className="h-8 text-sm" data-testid="input-hora-aviso" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Destinatarios (separados por coma)</Label>
            <Input value={cfg.to} onChange={(e) => setCfg((c) => ({ ...c, to: e.target.value }))} placeholder="correo1@chirino.cl, correo2@chirino.cl" className="h-8 text-sm" data-testid="input-aviso-to" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CC (opcional)</Label>
            <Input value={cfg.cc} onChange={(e) => setCfg((c) => ({ ...c, cc: e.target.value }))} className="h-8 text-sm" data-testid="input-aviso-cc" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={sendNow} disabled={busy} data-testid="btn-enviar-aviso-ahora">Enviar ahora</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="btn-guardar-aviso">{busy ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
